import { DropzoneComponent } from '@ksyos/react-dropzone-component';
import classNames from 'classnames';
import { Component } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { IFile } from '../../../../../common/interfaces/IFile';
import { AssetContentType } from '../../../../../common/enums/AssetContentType';
import { UploadStatus } from '../../../../../common/enums/UploadStatus';
import { ValidationErrorCode } from '../../../../../common/enums/ValidationErrorCode';
import { fetchJson } from '../../../../store/fetch';
import { translateKey } from '../../../../translate';
import { getFileType } from '../../../../utils/getFileType';
import { Icon, IconColor } from '../../atoms/icon/Icon';
import { List } from '../../molecules/list/List';
import { ListItem } from '../../molecules/listItem/ListItem';
import MediaObject from '../../molecules/mediaObject/MediaObject';
import { AssetThumbnail } from './AssetThumbnail';

export interface IFileUpload {
    index?: number;
    name: string;
    s3Id: string;
    s3UploadUrl: string;
    thumbnailDataUrl?: string;
}

interface IAssetDropzoneProps {
    acceptedFiles: string;
    alternativeUrl?: string;
    className?: string;
    clearAfterUpload?: boolean;
    dragFilesText?: string;
    group?: string;
    itemId?: string;
    maxFiles?: number;
    numberOfSavedAssets?: number;
    numberOfUploadingAssets?: number;
    onComplete?: () => void;
    onError?: (errorCode: string) => void;
    onFileUploadComplete: (file: IFile) => void;
    onRemoved?: (file: IFileUpload, isUploadInProgress: boolean) => void;
    onSending?: (anAddedFileUploadFailed: boolean) => void;
    onThumbnail?: (file: IFile, dataUrl: string) => void;
    placeholder?: string;
    setMaxAmountFiles: number;
}

interface IAssetDropzoneState {
    removedFilesWithoutId: string[];
    selectedFileId?: string;
    selectedFileIsPdf: boolean;
}

export class AssetDropzone extends Component<IAssetDropzoneProps, IAssetDropzoneState> {
    private dropzone: any;
    private filesAdded: IFile[] = [];
    private anAddedFileUploadFailed = false;
    private extraFilesOverLimit = 0;
    private fileIndex = 0;

    public constructor(props: IAssetDropzoneProps) {
        super(props);

        this.state = {
            removedFilesWithoutId: [],
            selectedFileIsPdf: false,
        };
    }

    public async UNSAFE_componentWillReceiveProps(nextProps: IAssetDropzoneProps) {
        if (nextProps.clearAfterUpload && nextProps.numberOfUploadingAssets === 0) {
            if (
                this.props.numberOfSavedAssets + this.dropzone.getAcceptedFiles().length ===
                nextProps.numberOfSavedAssets
            ) {
                this.dropzone.removeAllFiles();
            }
        }
    }

    public render() {
        return (
            <>
                <DropzoneComponent
                    className={classNames('asset-dropzone', this.props.className ? this.props.className : '')}
                    config={{
                        postUrl: '/api/asset/invalid',
                    }}
                    djsConfig={{
                        accept: async (
                            file: { index: number; name: string; s3Id?: string; s3UploadUrl?: string; type: string },
                            done: (message?: string) => void
                        ) => {
                            if (this.dropzone.files.length > this.dropzone.options.maxFiles) {
                                return;
                            }

                            file.index = this.fileIndex++;

                            const fileType = getFileType(file);

                            let status = 200;
                            let url = this.props.alternativeUrl
                                ? `${this.props.alternativeUrl}&`
                                : '/api/upload/uploadUrl?';
                            url += `contentType=${fileType}&index=${file.index}`; // We send the index, otherwise Safari will cache aggressively, and only send one request when there are more assets

                            try {
                                const response = await fetchJson(url, 'GET');
                                status = response.status;
                                const json = response.json;
                                if (status === 404) {
                                    throw new Error('404');
                                }
                                if (status >= 400) {
                                    let code = 'DEFAULT';
                                    if (json && json.code) {
                                        code = json.code;
                                    }
                                    throw new Error(code);
                                }
                                file.s3Id = json.id;
                                file.s3UploadUrl = json.url;
                                done();
                            } catch (error) {
                                console.error(error);
                                // Todo CS-4368: Show proper error message for end user
                            }
                        },
                        acceptedFiles: this.props.acceptedFiles,
                        addRemoveLinks: true,

                        dictDefaultMessage: renderToStaticMarkup(
                            <>
                                {
                                    <MediaObject
                                        mediaItem={
                                            <Icon
                                                className="dz-add-button"
                                                color={IconColor.primary}
                                                icon="add"
                                                size="large"
                                            />
                                        }
                                        title={this.props.placeholder || 'Sleep je bestanden in dit vak'}
                                        titleClassName="dropzone-title"
                                    >
                                        {!this.props.placeholder && (
                                            <List>
                                                <ListItem>zoek een bestand</ListItem>
                                            </List>
                                        )}
                                    </MediaObject>
                                }
                            </>
                        ),

                        dictFileTooBig: translateKey('dropzone.fileTooBig'),

                        dictInvalidFileType: translateKey('dropzone.invalidFileType'),

                        dictMaxFilesExceeded: translateKey(
                            `errorCodes.${ValidationErrorCode.TOO_MANY_ATTACHMENTS}`,
                            this.props.setMaxAmountFiles.toString()
                        ),

                        dictResponseError: translateKey('dropzone.serverNotConfigured'),

                        // mb
                        maxFiles: this.props.maxFiles,
                        maxFilesize: this.props.acceptedFiles.match(/\.(mp4|mov)$/i) ? 500 : 50,
                        maxThumbnailFilesize: 50,
                        method: 'PUT',
                        parallelUploads: 15,
                        paramName: 'file',
                        previewTemplate: renderToStaticMarkup(<AssetThumbnail />),
                        thumbnailHeight: 320,
                        thumbnailWidth: 320,
                        timeout: 1000 * 60 * 60,
                    }}
                    eventHandlers={{
                        addedfile: (file: IFile) => {
                            this.addOnChangeListenerToFileInput();

                            const fiftyMb = 50 * 1000 * 1000;
                            const fiveHundredMb = 500 * 1000 * 1000;
                            const maxSize =
                                file.type === AssetContentType.mov || file.type === AssetContentType.mp4
                                    ? fiveHundredMb
                                    : fiftyMb;
                            if (file.size > maxSize) {
                                this.anAddedFileUploadFailed = true;
                                this.dropzone.removeFile(file);
                                if (this.props.onError) {
                                    this.props.onError(translateKey('dropzone.fileTooBig'));
                                }

                                return;
                            }

                            if (this.extraFilesOverLimit > 0) {
                                this.extraFilesOverLimit--;
                                this.anAddedFileUploadFailed = true;

                                return this.dropzone.removeFile(file);
                            } else {
                                this.filesAdded.push(file);
                                const fileType = getFileType(file);
                                if (file.status === UploadStatus.added) {
                                    if (fileType === AssetContentType.mov) {
                                        this.dropzone.emit('thumbnail', file, '/assets/icon-svg/icon-mov.svg');
                                    } else if (fileType === AssetContentType.mp4) {
                                        this.dropzone.emit('thumbnail', file, '/assets/icon-svg/icon-mp4.svg');
                                    } else if (
                                        fileType === AssetContentType.doc ||
                                        fileType === AssetContentType.docx
                                    ) {
                                        this.dropzone.emit('thumbnail', file, '/assets/svg/icon-doc.svg');
                                    } else if (
                                        fileType === AssetContentType.xls ||
                                        fileType === AssetContentType.xlsx
                                    ) {
                                        this.dropzone.emit('thumbnail', file, '/assets/svg/icon-xls.svg');
                                    } else if (
                                        fileType === AssetContentType.ppt ||
                                        fileType === AssetContentType.pptx
                                    ) {
                                        this.dropzone.emit('thumbnail', file, '/assets/svg/icon-ppt.svg');
                                    }
                                }
                            }

                            this.anAddedFileUploadFailed = false;
                        },
                        complete: this.onDropzoneComplete.bind(this),
                        drop: (event: any) => {
                            const totalFiles =
                                this.dropzone.getAcceptedFiles().length + event.dataTransfer.files.length;
                            if (totalFiles > this.dropzone.options.maxFiles) {
                                this.extraFilesOverLimit = event.dataTransfer.files.length;

                                if (this.props.onError) {
                                    this.props.onError(
                                        translateKey(
                                            `errorCodes.${ValidationErrorCode.TOO_MANY_ATTACHMENTS}`,
                                            this.props.setMaxAmountFiles.toString()
                                        )
                                    );
                                }

                                this.anAddedFileUploadFailed = true;
                            } else if (this.props.onError) {
                                this.anAddedFileUploadFailed = false;
                                this.props.onError('');
                                // Todo CS-4368: This is not an error
                            }
                        },
                        error: this.onDropzoneError.bind(this),
                        init: this.onDropzoneInit.bind(this),
                        processing: (file: IFile) => {
                            this.dropzone.options.url = file.s3UploadUrl;
                        },
                        queuecomplete: async () => {
                            this.checkAndHandleFailedFiles();
                            this.addOnChangeListenerToFileInput();
                        },
                        removedfile: async (file: IFileUpload) => {
                            await this.setState(({ removedFilesWithoutId }) => ({
                                removedFilesWithoutId: removedFilesWithoutId.concat([file.s3Id]),
                            }));

                            this.filesAdded = this.filesAdded.filter((item) => item.s3Id !== file.s3Id);
                            const isUploadInProgress = this.filesAdded.some(
                                (item) => item.status !== UploadStatus.completed
                            );
                            if (this.props.onRemoved) {
                                await this.props.onRemoved(file, isUploadInProgress);
                            }
                        },
                        sending: (file: IFile, xhr: any) => {
                            const _send = xhr.send;
                            const fileType = getFileType(file);
                            xhr.setRequestHeader('Content-type', fileType);
                            xhr.send = () => {
                                _send.call(xhr, file);
                            };

                            this.checkAndHandleFailedFiles();
                            if (this.props.onSending) {
                                this.props.onSending(this.anAddedFileUploadFailed);
                            }
                        },
                        thumbnail: (file: IFile, dataUrl: string) => {
                            if (this.props.onThumbnail) {
                                this.props.onThumbnail(file, dataUrl);
                            }
                        },

                        uploadprogress: (file: IFile, progress: number, bytesSent: number) => {
                            this.onDropzoneUploadProgress(file, progress, bytesSent);
                        },
                    }}
                />
            </>
        );
    }

    private checkAndHandleFailedFiles() {
        const filesFailed = this.filesAdded.filter((file) => file.status === UploadStatus.error);
        if (filesFailed.length > 0) {
            this.anAddedFileUploadFailed = true;
            for (const file of filesFailed) {
                this.dropzone.removeFile(file);
            }

            this.filesAdded = this.filesAdded.filter((file) => file.status !== UploadStatus.error);
        }
    }

    private addOnChangeListenerToFileInput() {
        // This is triggered when the user has selected files via the input file dialog
        this.dropzone.hiddenFileInput.addEventListener('change', () => {
            if (this.dropzone.files.length > this.dropzone.options.maxFiles) {
                for (const file of this.filesAdded) {
                    this.dropzone.removeFile(file);
                }

                if (this.props.onError) {
                    this.props.onError(
                        translateKey(
                            `errorCodes.${ValidationErrorCode.TOO_MANY_ATTACHMENTS}`,
                            this.props.setMaxAmountFiles.toString()
                        )
                    );
                }
                this.filesAdded = [];
                this.addOnChangeListenerToFileInput();
            }
        });
    }

    private onDropzoneInit(dropzone: any) {
        this.dropzone = dropzone;
        this.addOnChangeListenerToFileInput();
    }

    private onDropzoneError(file: IFile, errormessage: any, xhr?: any) {
        this.dropzone.removeFile(file);

        if (errormessage === 'Upload canceled.') {
            return;
        }

        let detailedMessage: string | undefined;
        if (xhr) {
            detailedMessage = 'Server: ';
        } else {
            detailedMessage = '';
        }

        if (typeof errormessage === 'string') {
            detailedMessage += errormessage;
        } else if (errormessage.message) {
            detailedMessage += errormessage.message;
        } else {
            detailedMessage += JSON.stringify(errormessage);
        }

        if (this.props.onError) {
            this.props.onError(detailedMessage);
        }
    }

    private async onDropzoneComplete(file: IFile) {
        if (file.status === UploadStatus.success) {
            const fileType = getFileType(file);
            if (fileType === AssetContentType.imageHeic || fileType === AssetContentType.imageHeif) {
                const response = await this.createThumbnail(file);
                if (response && response.json?.url) {
                    const thumbnailDataUrl = response.json.url;
                    file.thumbnailDataUrl = thumbnailDataUrl;
                    this.dropzone.emit('thumbnail', file, thumbnailDataUrl);
                }
            }

            if (this.state.removedFilesWithoutId.includes(file.s3Id)) {
                await this.setState(({ removedFilesWithoutId }) => ({
                    removedFilesWithoutId: removedFilesWithoutId.filter((s3Id) => s3Id !== file.s3Id),
                }));
                this.dropzone.removeFile(file);

                return;
            }

            const fileAdded = this.filesAdded.find((item) => item.s3Id === file.s3Id);
            if (fileAdded) {
                fileAdded.status = UploadStatus.completed;
            }

            this.props.onFileUploadComplete(file);
            if (this.filesAdded.every((item) => item.status === UploadStatus.completed)) {
                if (this.props.onComplete) {
                    this.props.onComplete();
                }
                this.filesAdded = [];
            }
        }
    }

    private onDropzoneUploadProgress(file: IFile, progress: number, _bytesSent: number) {
        const fileType = getFileType(file);
        if (fileType === AssetContentType.imageTif || fileType === AssetContentType.imageTiff) {
            this.dropzone.emit('thumbnail', file, '/assets/icon-svg/icon-tiff.svg');
        } else if (fileType === AssetContentType.imageHeic || fileType === AssetContentType.imageHeif) {
            this.dropzone.emit('thumbnail', file, '/assets/icon-svg/icon-heic.svg');
        }
        if (progress === 100) {
            if (file.previewElement.querySelector('.dz-remove')) {
                file.previewElement.querySelector('.dz-remove').outerHTML = '';
            }
        }
    }

    private async createThumbnail(file: IFile) {
        let response;
        try {
            response = await fetchJson('/api/upload/thumbnail', 'POST', { filename: file.name, id: file.s3Id });
        } catch {
            response = null;
        }

        return response;
    }
}
